/**
 * Metronome (W1-14, A§8, D§5.9): synthesised clicks, accented on bar starts. The editor supplies the beat times
 * (from the tempo map and its meter marks) through `setMetronome(on, beatTimes)`; the scheduler places one click per
 * beat with `start(when)` exactly like hitsounds (no per-type delay: the synthesised click has an immediate onset).
 */

import type { Sec } from '../core/time/types';
import { synthBuffer, type BufferFactory } from './synth';

export type BeatTimesFn = (t0: Sec, t1: Sec) => ReadonlyArray<{ readonly t: Sec; readonly accent: boolean }>;

export interface MetronomeClick {
  readonly t: Sec;
  readonly accent: boolean;
}

export class Metronome {
  private on = false;
  private beats: BeatTimesFn | null = null;

  set(on: boolean, beatTimes: BeatTimesFn): void {
    this.on = on;
    this.beats = beatTimes;
  }

  get enabled(): boolean {
    return this.on && this.beats !== null;
  }

  /** Clicks with chart time in [t0, t1), in time order (the provider's list is filtered and sorted defensively). */
  clicksIn(t0: Sec, t1: Sec): MetronomeClick[] {
    const fn = this.beats;
    if (!this.on || fn === null || !(t1 > t0)) return [];
    const out: MetronomeClick[] = [];
    for (const b of fn(t0, t1)) {
      if (Number.isFinite(b.t) && b.t >= t0 && b.t < t1) out.push({ t: b.t, accent: b.accent });
    }
    out.sort((a, b) => a.t - b.t);
    return out;
  }
}

/** The two metronome sounds of one context. */
export class ClickSounds {
  readonly normal: AudioBuffer;
  readonly accent: AudioBuffer;
  readonly factory: BufferFactory;

  constructor(factory: BufferFactory) {
    this.factory = factory;
    this.normal = synthBuffer(factory, 'metronome');
    this.accent = synthBuffer(factory, 'metronomeAccent');
  }

  of(accent: boolean): AudioBuffer {
    return accent ? this.accent : this.normal;
  }
}

/**
 * Beat times of a constant tempo (`bpm`, beat 0 at chart time `firstBeat`, accent every `beatsPerBar` beats, counted
 * from beat 0). Useful before a tempo map exists (wizard, calibration previews).
 */
export function regularBeats(bpm: number, firstBeat: Sec = 0, beatsPerBar = 4): BeatTimesFn {
  if (!(bpm > 0) || !Number.isFinite(bpm)) throw new RangeError(`regularBeats: bad BPM ${bpm}`);
  const spb = 60 / bpm;
  const bar = Math.max(1, Math.round(beatsPerBar));
  return (t0, t1) => {
    const out: MetronomeClick[] = [];
    if (!(t1 > t0)) return out;
    const k0 = Math.ceil((t0 - firstBeat) / spb - 1e-9);
    for (let k = k0; ; k++) {
      const t = firstBeat + k * spb;
      if (t >= t1) break;
      if (t >= t0) out.push({ t, accent: ((k % bar) + bar) % bar === 0 });
    }
    return out;
  };
}
